import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/ui/Modal";
import { ItemPopover } from "../../../components/atomic/itemPopover";
import { updateMetadata } from "../../../api/reportsApi";
import { Metadata } from "../../../types/Metadata";
import { v4 as uuidv4 } from "uuid";
import { field } from "../../../components/ui/styles";
import { isMultiDayVisit } from "../../../utils/visitDate";
import { fromDateInputValue, toDateInputValue } from "../../../utils/dateInput";


interface MetadataEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: Metadata;
  reportId: string;
  isDisabled?: boolean;
  // CM: obie daty to okres analizy, więc zawsze dwa pola
  isCentralMonitoring?: boolean;
}

export const MetadataEditModal = ({
  isOpen,
  onClose,
  metadata,
  reportId,
  isDisabled = false,
  isCentralMonitoring = false,
}: MetadataEditModalProps) => {
  const queryClient = useQueryClient();
  
  // Form state
  const [formData, setFormData] = useState<Metadata>({...metadata});
  const [isMultiDay, setIsMultiDay] = useState(() => isMultiDayVisit(metadata.visitDate, metadata.visitDateEnd));
  const showEndDate = isCentralMonitoring || isMultiDay;

  // Reset form data when modal opens or metadata changes
  useEffect(() => {
    if (isOpen) {
      setFormData({...metadata});
    }
  }, [isOpen, metadata]);

  // Calculate recruitment status when relevant fields change
  useEffect(() => {
    if (formData.randomized && formData.declaredParticipants) {
      const calculatedValue = Math.round(
        (formData.randomized / formData.declaredParticipants) * 100
      );
      if (calculatedValue !== formData.recrutationStatus && calculatedValue > 0) {
        setFormData(prev => ({
          ...prev,
          recrutationStatus: calculatedValue
        }));
      }
    }
  }, [formData.randomized, formData.declaredParticipants]);

  const updateField = (field: keyof Metadata, value: any) => {
    if (isDisabled) return;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const inputClassName = `w-full border border-[var(--sc-border)] rounded-lg px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)] ${isDisabled ? 'bg-[var(--sc-bg)] cursor-not-allowed text-[var(--sc-muted)]' : 'bg-[var(--sc-surface)]'}`;
  const textareaClassName = `w-full border border-[var(--sc-border)] rounded-lg px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)] ${isDisabled ? 'bg-[var(--sc-bg)] cursor-not-allowed text-[var(--sc-muted)]' : 'bg-[var(--sc-surface)]'}`;

  const { mutateAsync: saveMetadata, isLoading } = useMutation(
    async (updatedMetadata: Metadata) => {
      const correlationId = uuidv4();
      return updateMetadata(reportId, updatedMetadata, correlationId);
    },
    {
      onSuccess: () => {
        // Comprehensive invalidation to ensure all related queries are refreshed
        queryClient.resetQueries({ queryKey: [reportId, "Metadata"] });
        queryClient.resetQueries({ queryKey: [reportId, "Report"] });
                
        onClose();
      },
    }
  );

  const handleSave = async () => {
    // wizyta jednodniowa: bez daty końca (SR-012).
    // Numer wizyty jest tylko w nagłówku raportu: null = backend nie zmienia numeru ani nazwy raportu
    const payload = { ...formData, visitNumber: null };
    await saveMetadata(showEndDate ? payload : { ...payload, visitDateEnd: undefined });
  };

  const handleCancel = () => {
    setFormData({...metadata});
    setIsMultiDay(isMultiDayVisit(metadata.visitDate, metadata.visitDateEnd));
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleCancel} size="xl">
      <ModalHeader title={isDisabled ? "Podgląd metadanych raportu" : "Edytuj raportu"} onClose={handleCancel} />
      
      <ModalBody>
        <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-2" style={{ minWidth: '800px' }}>
          {/* Protocol Information */}
          <div>
                {/* Participants Text Fields */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Uczestnicy tekstowo</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Uczestnicy CRO
                </label>
                <textarea
                  className={textareaClassName}
                  rows={3}
                  value={formData.croParticipants || ""}
                  onChange={(e) => updateField("croParticipants", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Uczestnicy ośrodka
                </label>
                <textarea
                  className={textareaClassName}
                  rows={3}
                  value={formData.resortParticipants || ""}
                  onChange={(e) => updateField("resortParticipants", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Informacje o protokole</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Numer protokołu
                </label>
                <input
                  type="text"
                  className={inputClassName}
                  value={formData.protocolNumber || ""}
                  onChange={(e) => updateField("protocolNumber", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Akronim badania
                </label>
                <input
                  type="text"
                  className={inputClassName}
                  value={formData.studyAcronym || ""}
                  onChange={(e) => updateField("studyAcronym", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Numer ośrodka
                </label>
                <input
                  type="text"
                  className={inputClassName}
                  value={formData.resortNumber || ""}
                  onChange={(e) => updateField("resortNumber", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Główny badacz
                </label>
                <input
                  type="text"
                  className={inputClassName}
                  value={formData.mainResearcher || ""}
                  onChange={(e) => updateField("mainResearcher", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>

          {/* Visit Information */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Informacje o wizycie</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  {isCentralMonitoring ? "Okres od" : "Data wizyty"}
                </label>
                <input
                  type="date"
                  className={inputClassName}
                  value={toDateInputValue(formData.visitDate)}
                  onChange={(e) => updateField("visitDate", fromDateInputValue(e.target.value))}
                  disabled={isDisabled}
                />
                {!isCentralMonitoring && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-[var(--sc-muted)] cursor-pointer">
                    <input
                      type="checkbox"
                      className={field.checkbox}
                      checked={isMultiDay}
                      onChange={(e) => setIsMultiDay(e.target.checked)}
                      disabled={isDisabled}
                    />
                    Wizyta wielodniowa
                  </label>
                )}
              </div>
              {showEndDate && (
                <div>
                  <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                    {isCentralMonitoring ? "Okres do" : "Data zakończenia wizyty"}
                  </label>
                  <input
                    type="date"
                    className={inputClassName}
                    value={toDateInputValue(formData.visitDateEnd)}
                    onChange={(e) => updateField("visitDateEnd", fromDateInputValue(e.target.value))}
                    disabled={isDisabled}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Uczestnicy</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Zadeklarowani uczestnicy
                  <ItemPopover text="Wpisz liczbę uczestników zadeklarowanych przez Badacza i Ośrodek w umowie CSA." />
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.declaredParticipants || ""}
                  onChange={(e) => updateField("declaredParticipants", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Skriningowani
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.afterScreening || ""}
                  onChange={(e) => updateField("afterScreening", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Zrandomizowani
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.randomized || ""}
                  onChange={(e) => updateField("randomized", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Z niepowodzeniem skriningu
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.screenFailure || ""}
                  onChange={(e) => updateField("screenFailure", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  W trakcie leczenia
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.duringTreatment || ""}
                  onChange={(e) => updateField("duringTreatment", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  W trakcie obserwacji
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.duringFollowUp || ""}
                  onChange={(e) => updateField("duringFollowUp", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Kończący badania przedwcześnie EOT
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.prematureCompletion || ""}
                  onChange={(e) => updateField("prematureCompletion", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Zakończony udział EOS
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.completedStudy || ""}
                  onChange={(e) => updateField("completedStudy", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>

          {/* Study Status */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Status badania</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Status rekrutacji (%)
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.recrutationStatus || ""}
                  onChange={(e) => updateField("recrutationStatus", parseInt(e.target.value) || 0)}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  Odchylenia od protokołu
                  <ItemPopover text="Wpisz liczbę odchyleń od protokołu zidentyfikowanych od poprzedniej wizyty." />
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.protocolDeviation ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateField("protocolDeviation", raw === "" ? undefined : parseInt(raw));
                  }}
                  disabled={isDisabled}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1">
                  SAE
                  <ItemPopover text="Wpisz liczbę SAE zidentyfikowanych od poprzedniej wizyty." />
                </label>
                <input
                  type="number"
                  className={inputClassName}
                  value={formData.reportedSAE ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateField("reportedSAE", raw === "" ? undefined : parseInt(raw));
                  }}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>

      

          {/* Final Comment */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-3">Komentarz końcowy</h3>
            <textarea
              className={textareaClassName}
              rows={4}
              value={formData.finalComment || ""}
              onChange={(e) => updateField("finalComment", e.target.value)}
              placeholder="Wprowadź komentarz końcowy..."
              disabled={isDisabled}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter
        onSave={isDisabled ? undefined : handleSave}
        onCancel={handleCancel}
        saveLoading={isLoading}
        saveDisabled={isLoading || isDisabled}
      />
    </Modal>
  );
};